import React, { useEffect, useState, useCallback } from "react";
import { MessageCircle, RefreshCw, ChevronDown } from "lucide-react";
import { supabase } from "../../lib/supabase";
import { OrderStatusBadge, Spinner, EmptyState } from "../../components/common/UI";
import toast from "react-hot-toast";

const STATUS_OPTIONS = ["all", "pending", "confirmed", "preparing", "ready", "delivered", "cancelled"];

const buildWhatsAppMessage = (order, items) => {
  const lines = [
    `🍽️ *NEW ORDER — Khana Spot*`,
    `━━━━━━━━━━━━━━━━━━━━`,
    `📋 *Order ID:* #${order.id.slice(0, 8).toUpperCase()}`,
    ``,
    `👤 *Customer Details*`,
    `Name: ${order.customer_name}`,
    `Phone: ${order.customer_phone}`,
    `Address: ${order.customer_address}`,
    order.notes ? `Notes: ${order.notes}` : null,
    ``,
    `🛒 *Order Items*`,
    ...items.map((i) => `• ${i.menu_item_name} × ${i.quantity} = ₨${i.subtotal}`),
    ``,
    `━━━━━━━━━━━━━━━━━━━━`,
    `💰 *Subtotal:* ₨${(order.total - (order.delivery_fee || 0)).toLocaleString()}`,
    order.delivery_fee > 0 ? `🛵 *Delivery Fee:* ₨${order.delivery_fee}` : null,
    `✅ *Total:* ₨${order.total?.toLocaleString()}`,
    ``,
    `📅 *Order Time:* ${new Date(order.created_at).toLocaleString("en-PK")}`,
  ].filter(Boolean);
  return lines.join("\n");
};

export default function AdminOrders() {
  const [orders, setOrders] = useState([]);
  const [orderItems, setOrderItems] = useState({});
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState("all");
  const [expandedOrder, setExpandedOrder] = useState(null);

  const fetchOrders = useCallback(async () => {
    setLoading(true);
    let query = supabase
      .from("orders")
      .select("*, restaurants(name, contact, whatsapp), users(name, email)")
      .order("created_at", { ascending: false });
    if (statusFilter !== "all") query = query.eq("status", statusFilter);
    const { data } = await query;
    setOrders(data || []);
    setLoading(false);
  }, [statusFilter]);

  useEffect(() => { fetchOrders(); }, [fetchOrders]);

  const fetchItems = async (orderId) => {
    if (orderItems[orderId]) { setExpandedOrder(expandedOrder === orderId ? null : orderId); return; }
    const { data } = await supabase.from("order_items").select("*").eq("order_id", orderId);
    setOrderItems((p) => ({ ...p, [orderId]: data || [] }));
    setExpandedOrder(expandedOrder === orderId ? null : orderId);
  };

  const updateStatus = async (orderId, newStatus) => {
    const { error } = await supabase.from("orders").update({ status: newStatus }).eq("id", orderId);
    if (error) { toast.error("Failed to update status"); return; }
    setOrders((prev) => prev.map((o) => o.id === orderId ? { ...o, status: newStatus } : o));
    toast.success(`Order marked as ${newStatus}`);
  };

  const sendWhatsApp = async (order) => {
    let items = orderItems[order.id];
    if (!items) {
      const { data } = await supabase.from("order_items").select("*").eq("order_id", order.id);
      items = data || [];
      setOrderItems((p) => ({ ...p, [order.id]: items }));
    }
    const restaurantNumber = (order.restaurants?.whatsapp || order.restaurants?.contact || "").replace(/\D/g, "");
    if (!restaurantNumber) { toast.error("No WhatsApp number for this restaurant"); return; }
    const message = buildWhatsAppMessage(order, items);
    window.open(`https://wa.me/${restaurantNumber}?text=${encodeURIComponent(message)}`, "_blank");
    await supabase.from("orders").update({ whatsapp_sent: true }).eq("id", order.id);
    setOrders((prev) => prev.map((o) => o.id === order.id ? { ...o, whatsapp_sent: true } : o));
    toast.success("Opening WhatsApp...");
  };

  return (
    <div className="space-y-6 page-enter">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-display text-2xl font-bold text-gray-900">Orders</h1>
          <p className="text-gray-500 text-sm">{orders.length} order{orders.length !== 1 ? "s" : ""} found</p>
        </div>
        <button onClick={fetchOrders} className="flex items-center gap-2 btn-ghost border border-gray-200">
          <RefreshCw className="w-4 h-4" /> Refresh
        </button>
      </div>

      {/* Status filter */}
      <div className="flex gap-2 overflow-x-auto pb-1">
        {STATUS_OPTIONS.map((s) => (
          <button
            key={s}
            onClick={() => setStatusFilter(s)}
            className={`flex-shrink-0 px-4 py-2 rounded-full text-sm font-medium capitalize border transition-colors ${
              statusFilter === s ? "bg-orange-500 text-white border-orange-500" : "border-gray-200 text-gray-600 hover:border-orange-300"
            }`}
          >
            {s}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="flex justify-center py-16"><Spinner size="lg" /></div>
      ) : orders.length === 0 ? (
        <EmptyState icon="📦" title="No orders found" description="Orders will appear here when customers place them." />
      ) : (
        <div className="space-y-3">
          {orders.map((order) => (
            <div key={order.id} className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
              <div className="p-4 sm:p-5">
                <div className="flex items-start justify-between gap-3 flex-wrap">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-mono text-xs bg-gray-100 text-gray-600 px-2 py-0.5 rounded-lg">
                        #{order.id.slice(0, 8).toUpperCase()}
                      </span>
                      <OrderStatusBadge status={order.status} />
                      {order.whatsapp_sent && (
                        <span className="badge bg-green-100 text-green-700">✓ WhatsApp Sent</span>
                      )}
                    </div>
                    <div className="mt-2 grid grid-cols-1 sm:grid-cols-3 gap-2 text-sm">
                      <div>
                        <span className="text-gray-400 text-xs">Customer</span>
                        <p className="font-medium text-gray-900">{order.customer_name}</p>
                        <p className="text-gray-500 text-xs">{order.customer_phone}</p>
                      </div>
                      <div>
                        <span className="text-gray-400 text-xs">Restaurant</span>
                        <p className="font-medium text-gray-900">{order.restaurants?.name}</p>
                      </div>
                      <div>
                        <span className="text-gray-400 text-xs">Total</span>
                        <p className="font-bold text-orange-600">₨{order.total?.toLocaleString()}</p>
                        <p className="text-xs text-gray-400">{new Date(order.created_at).toLocaleString("en-PK")}</p>
                      </div>
                    </div>
                    {order.customer_address && (
                      <p className="text-xs text-gray-500 mt-1.5 flex gap-1">📍 {order.customer_address}</p>
                    )}
                  </div>
                </div>

                {/* Actions */}
                <div className="flex flex-wrap gap-2 mt-4 pt-4 border-t border-gray-100">
                  <button
                    onClick={() => sendWhatsApp(order)}
                    className="flex items-center gap-1.5 bg-green-500 hover:bg-green-600 text-white text-xs font-semibold px-4 py-2 rounded-xl transition-colors"
                  >
                    <MessageCircle className="w-3.5 h-3.5" />
                    Send to Restaurant via WhatsApp
                  </button>
                  <button
                    onClick={() => fetchItems(order.id)}
                    className="flex items-center gap-1.5 text-xs font-medium text-gray-600 hover:text-gray-800 border border-gray-200 px-3 py-2 rounded-xl transition-colors"
                  >
                    <ChevronDown className={`w-3.5 h-3.5 transition-transform ${expandedOrder === order.id ? "rotate-180" : ""}`} />
                    View Items
                  </button>
                  <select
                    value={order.status}
                    onChange={(e) => updateStatus(order.id, e.target.value)}
                    className="text-xs border border-gray-200 rounded-xl px-3 py-2 focus:outline-none focus:ring-2 focus:ring-orange-400 bg-white text-gray-700"
                  >
                    {["pending", "confirmed", "preparing", "ready", "delivered", "cancelled"].map((s) => (
                      <option key={s} value={s} className="capitalize">{s.charAt(0).toUpperCase() + s.slice(1)}</option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Expanded items */}
              {expandedOrder === order.id && orderItems[order.id] && (
                <div className="border-t border-gray-100 bg-gray-50 px-5 py-4">
                  <p className="text-xs font-semibold text-gray-500 uppercase mb-3">Order Items</p>
                  <div className="space-y-2">
                    {orderItems[order.id].map((item) => (
                      <div key={item.id} className="flex justify-between text-sm">
                        <span className="text-gray-700">{item.menu_item_name} <span className="text-gray-400">× {item.quantity}</span></span>
                        <span className="font-semibold text-gray-900">₨{item.subtotal}</span>
                      </div>
                    ))}
                    {order.notes && (
                      <p className="text-xs text-gray-500 mt-2 pt-2 border-t border-gray-200">
                        📝 Notes: {order.notes}
                      </p>
                    )}
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
